import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export function useJobs() {
  const { user } = useAuth();
  const userId = user?.id;
  const [personalizedJobs, setPersonalizedJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch all active jobs (for HM view)
  const fetchAllJobs = useCallback(async () => {
    const { data } = await supabase
      .from('jobs')
      .select('*')
      .eq('is_active', true)
      .order('legacy_id');
    if (data) setAllJobs(data);
  }, []);

  // Fetch personalized jobs for candidate (via RPC)
  const fetchPersonalizedJobs = useCallback(async () => {
    if (!userId) return;
    if (personalizedJobs.length === 0) setLoading(true);
    const { data, error } = await supabase
      .rpc('get_personalized_jobs', { p_candidate_id: userId });
    if (!error && data) {
      setPersonalizedJobs(data);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Fetch jobs owned by this HM
  const fetchMyJobs = useCallback(async () => {
    if (!userId) return [];
    const { data } = await supabase
      .from('jobs')
      .select('*')
      .eq('hiring_manager_id', userId)
      .eq('is_active', true)
      .order('legacy_id');
    return data || [];
  }, [userId]);

  // Claim a job as HM
  async function claimJob(jobId) {
    if (!user) return;
    const { error } = await supabase
      .from('jobs')
      .update({ hiring_manager_id: user.id, updated_at: new Date().toISOString() })
      .eq('id', jobId);
    if (!error) {
      await fetchAllJobs();
    }
    return { error };
  }

  useEffect(() => {
    fetchAllJobs();
  }, [fetchAllJobs]);

  async function dismissJob(jobId, reason) {
    if (!user) return { error: 'Not authenticated' };
    const { error } = await supabase
      .from('job_dismissals')
      .insert({
        candidate_id: user.id,
        job_id: jobId,
        reason,
      });
    return { error: error?.message };
  }

  return {
    personalizedJobs,
    allJobs,
    loading,
    fetchPersonalizedJobs,
    fetchAllJobs,
    fetchMyJobs,
    claimJob,
    dismissJob,
  };
}
